import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const useFavorites = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: favorites = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["favorites", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("user_favorites")
        .select(`
          id,
          provider_id,
          created_at,
          providers (
            id,
            name,
            display_name,
            location,
            image_url,
            specialties,
            slug,
            brand_primary_color,
            brand_secondary_color,
            brand_font,
            brand_header_text_color,
            brand_header_icon_text_color,
            brand_header_letter_spacing
          )
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  const addFavoriteMutation = useMutation({
    mutationFn: async (providerId: string) => {
      if (!user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("user_favorites")
        .insert({
          user_id: user.id,
          provider_id: providerId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favorites", user?.id] });
    },
  });

  // Set a provider as the only favorite (removes all others first)
  const setAsFavoriteMutation = useMutation({
    mutationFn: async (providerId: string) => {
      if (!user) throw new Error("User not authenticated");

      // Delete all existing favorites for this user
      const { error: deleteError } = await supabase
        .from("user_favorites")
        .delete()
        .eq("user_id", user.id);

      if (deleteError) throw deleteError;

      // Add the new favorite
      const { error: insertError } = await supabase
        .from("user_favorites")
        .insert({
          user_id: user.id,
          provider_id: providerId,
        });

      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favorites", user?.id] });
    },
  });

  const removeFavoriteMutation = useMutation({
    mutationFn: async (providerId: string) => {
      if (!user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("user_favorites")
        .delete()
        .eq("user_id", user.id)
        .eq("provider_id", providerId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favorites", user?.id] });
    },
  });

  const isFavorite = (providerId: string) => {
    return favorites.some((fav) => fav.provider_id === providerId);
  };

  const toggleFavorite = (providerId: string) => {
    if (isFavorite(providerId)) {
      removeFavoriteMutation.mutate(providerId);
    } else {
      addFavoriteMutation.mutate(providerId);
    }
  };

  return {
    favorites,
    isLoading,
    error,
    addFavorite: addFavoriteMutation.mutate,
    removeFavorite: removeFavoriteMutation.mutate,
    setAsFavorite: setAsFavoriteMutation.mutate,
    toggleFavorite,
    isFavorite,
    isAddingFavorite: addFavoriteMutation.isPending,
    isRemovingFavorite: removeFavoriteMutation.isPending,
    isSettingFavorite: setAsFavoriteMutation.isPending,
  };
};
